import React, { useState, useRef } from 'react';
import { Upload, Music, Trash2, Play, Square, Loader2, Volume2, Save } from 'lucide-react';
import api from '../api';

const BackgroundNoisesTab = ({ agent, formData, setFormData, onSubmit }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const audioRef = useRef(null);

    const backgroundAudioEnabled = formData.background_audio_enabled ?? false;
    const backgroundAudioFileUrl = formData.background_audio_file_url;
    const backgroundAudioVolume = formData.background_audio_volume ?? 0.3;

    const handleToggle = (e) => {
        e.preventDefault();
        setFormData({
            ...formData,
            background_audio_enabled: !backgroundAudioEnabled
        });
        setError(null);
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'];
        if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|m4a|aac)$/i)) {
            setError('Unsupported file type. Please upload MP3, WAV, OGG, M4A, or AAC files.');
            return;
        }

        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            setError('File too large. Maximum size is 10MB.');
            return;
        }

        if (!agent?.id) {
            setError('Please save the agent first before uploading audio files.');
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post(`/agents/${agent.id}/background-audio/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setFormData({
                ...formData,
                background_audio_file_url: response.data.file_url
            });

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.detail || 'Failed to upload audio file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!agent?.id || !backgroundAudioFileUrl) return;

        if (!window.confirm('Are you sure you want to delete this background audio file?')) {
            return;
        }

        try {
            await api.delete(`/agents/${agent.id}/background-audio`);
            setFormData({
                ...formData,
                background_audio_file_url: null
            });
            // Stop audio if playing
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                setIsPlaying(false);
            }
        } catch (err) {
            console.error('Delete error:', err);
            setError(err.response?.data?.detail || 'Failed to delete audio file');
        }
    };

    const handlePlayPreview = () => {
        if (!backgroundAudioFileUrl) return;

        if (!audioRef.current) {
            const audio = new Audio(backgroundAudioFileUrl);
            audio.addEventListener('ended', () => setIsPlaying(false));
            audio.addEventListener('error', () => {
                setError('Failed to play audio preview');
                setIsPlaying(false);
            });
            audioRef.current = audio;
        }

        if (isPlaying) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        } else {
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto min-h-0 p-6"
            style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 280px)'
            }}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Music className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">Background Audio</h3>
                            <p className="text-sm text-muted-foreground">Add ambient sounds that play when the agent is speaking</p>
                        </div>
                    </div>

                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div>
                            <label className="text-sm font-medium text-foreground">Enable Background Audio</label>
                            <p className="text-xs text-muted-foreground mt-1">
                                When enabled, the selected audio will play in the background during agent speech
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                backgroundAudioEnabled ? 'bg-primary' : 'bg-muted'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    backgroundAudioEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Volume Control */}
                    {backgroundAudioEnabled && (
                        <div className="mt-4 p-4 bg-muted/20 rounded-lg border border-border/50">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-foreground">Volume</label>
                                <span className="text-xs text-muted-foreground">{Math.round(backgroundAudioVolume * 100)}%</span>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={backgroundAudioVolume}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        background_audio_volume: parseFloat(e.target.value)
                                    });
                                }}
                                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                <span>0%</span>
                                <span>50%</span>
                                <span>100%</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Upload Section */}
                {backgroundAudioEnabled && (
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                        <h4 className="text-lg font-semibold text-foreground mb-4">Upload Audio File</h4>
                        
                        {!backgroundAudioFileUrl ? (
                            <div className="space-y-4">
                                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                                    <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                                    <p className="text-sm text-foreground mb-2">Upload background audio file</p>
                                    <p className="text-xs text-muted-foreground mb-4">
                                        Supported formats: MP3, WAV, OGG, M4A, AAC (Max 10MB)
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        id="audio-upload"
                                    />
                                    <label
                                        htmlFor="audio-upload"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all cursor-pointer"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                Choose File
                                            </>
                                        )}
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Current File Display */}
                                <div className="bg-muted/20 rounded-lg border border-border/50 p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                <Volume2 className="w-5 h-5 text-primary" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    Background Audio File
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {backgroundAudioFileUrl.split('/').pop()}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={handlePlayPreview}
                                                className="p-2 hover:bg-muted rounded-lg transition-colors"
                                                title={isPlaying ? "Stop Preview" : "Play Preview"}
                                            >
                                                {isPlaying ? (
                                                    <Square className="w-4 h-4 text-foreground" />
                                                ) : (
                                                    <Play className="w-4 h-4 text-foreground" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                                                title="Delete File"
                                            >
                                                <Trash2 className="w-4 h-4 text-destructive" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Replace File */}
                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                        id="audio-replace"
                                    />
                                    <label
                                        htmlFor="audio-replace"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all cursor-pointer text-sm"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                Replace File
                                            </>
                                        )}
                                    </label>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                                <p className="text-sm text-destructive">{error}</p>
                            </div>
                        )}
                    </div>
                )}

                {!backgroundAudioEnabled && (
                    <div className="bg-muted/20 rounded-lg border border-border/50 p-8 text-center">
                        <Music className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-sm text-muted-foreground">
                            Enable background audio to upload and configure ambient sounds
                        </p>
                    </div>
                )}

                {/* Save Button */}
                {agent?.id && (
                    <div className="flex justify-end pt-4 border-t border-border/50">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onSubmit) {
                                    // Create a synthetic submit event
                                    const syntheticEvent = {
                                        preventDefault: () => {},
                                        target: e.target.closest('form') || e.target,
                                        currentTarget: e.target.closest('form') || e.target
                                    };
                                    onSubmit(syntheticEvent);
                                }
                            }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 font-medium"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BackgroundNoisesTab;

